// CourtEdge Workspace — Basketball intelligence (WNBA + NBA) with inline game canvas.

import { useState, useEffect, useRef } from 'react';
import { SkeletonGameCard } from '../../components/common/Skeleton';
import { format, addDays, subDays } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ChevronLeft, ChevronRight, RefreshCw, Activity, CircleDot,
} from 'lucide-react';
import { useOSStore } from '../../store/useOSStore';
import { wnbaApi, nbaApi } from '../../api/client';
import { ModuleWorkspace } from '../../components/workspace/ModuleWorkspace';
import { GameIntelligenceCanvas } from '../../components/workspace/GameIntelligenceCanvas';
import { PulseUplink } from '../../components/workspace/PulseUplink';
import { todayPST } from '../../utils/format';
import type { WNBAGameContext, WNBAInjuryReport, NBAGameContext, NBAInjuryReport, BetRecommendation } from '../../types';

type ActiveLeague = 'wnba' | 'nba';

// ── Compact WNBA slate card ───────────────────────────────────────────────────

const AUDIT_BADGE: Record<string, { label: string; cls: string }> = {
  LEAN:    { label: 'LEAN',    cls: 'bg-warn/15 border-warn/30 text-warn' },
  NO_BET:  { label: 'NO BET', cls: 'bg-surface-3 border-surface-4 text-gray-400' },
  INVALID: { label: 'BLOCKED', cls: 'bg-loss/15 border-loss/30 text-loss' },
};

function WNBASlateCard({
  context,
  recommendations,
  injuries,
  auditStatus,
  isActive,
  onClick,
}: {
  context: WNBAGameContext;
  recommendations: BetRecommendation[];
  injuries: WNBAInjuryReport[];
  auditStatus?: string;
  isActive: boolean;
  onClick: () => void;
}) {
  const { game, odds } = context;
  const { awayTeam, homeTeam, status, awayScore, homeScore } = game;
  const isLive = status.state === 'in';
  const isPost = status.state === 'post';

  const strongRec = recommendations.find((r) => r.decisionStatus === 'STRONG_BET');
  const qualifiedRec = recommendations.find((r) => r.decisionStatus === 'QUALIFIED_BET');
  const bestRec = strongRec ?? qualifiedRec;

  const awayInjuries = injuries.filter((i) => i.teamId === awayTeam.id && (i.status === 'Out' || i.status === 'Questionable'));
  const homeInjuries = injuries.filter((i) => i.teamId === homeTeam.id && (i.status === 'Out' || i.status === 'Questionable'));

  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left px-3 py-2.5 border-b border-surface-4 transition-colors',
        isActive
          ? 'bg-warn/8 border-l-2 border-l-warn'
          : 'hover:bg-surface-1 border-l-2 border-l-transparent',
      )}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          {isLive ? (
            <>
              <span className="w-1.5 h-1.5 rounded-full bg-win animate-pulse flex-shrink-0" />
              <span className="text-[10px] font-semibold text-win">
                {status.period ? `Q${status.period}` : 'Live'}{status.clock ? ` ${status.clock}` : ''}
              </span>
            </>
          ) : (
            <span className={clsx('text-[10px] font-medium', isPost ? 'text-gray-600' : 'text-gray-500')}>
              {isPost ? 'Final' : status.detail || 'Scheduled'}
            </span>
          )}
        </div>
        {bestRec ? (
          <span className={clsx(
            'text-[9px] font-bold px-1 py-0.5 rounded border',
            strongRec ? 'bg-win/15 border-win/40 text-win' : 'bg-brand/15 border-brand/40 text-brand-light',
          )}>
            {strongRec ? 'STRONG' : 'QUALIFIED'}
          </span>
        ) : auditStatus && AUDIT_BADGE[auditStatus] ? (
          <span className={clsx('text-[9px] font-bold px-1 py-0.5 rounded border', AUDIT_BADGE[auditStatus].cls)}>
            {AUDIT_BADGE[auditStatus].label}
          </span>
        ) : null}
      </div>

      <div className="space-y-0.5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-300 truncate mr-2">
            {awayTeam.abbreviation}
            <span className="text-gray-500 ml-1 font-normal text-[10px]">{awayTeam.name.split(' ').pop()}</span>
            {awayInjuries.length > 0 && <span className="text-warn ml-1 text-[9px]">⚠</span>}
          </span>
          {(isLive || isPost) && awayScore !== undefined ? (
            <span className={clsx('text-sm font-bold tabular-nums', isPost && awayScore > (homeScore ?? 0) ? 'text-win' : 'text-gray-400')}>
              {awayScore}
            </span>
          ) : odds?.awayMoneyline !== undefined ? (
            <span className="text-[10px] font-mono text-gray-500 tabular-nums flex-shrink-0">
              {odds.awayMoneyline > 0 ? '+' : ''}{odds.awayMoneyline}
            </span>
          ) : null}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-300 truncate mr-2">
            {homeTeam.abbreviation}
            <span className="text-gray-500 ml-1 font-normal text-[10px]">{homeTeam.name.split(' ').pop()}</span>
            {homeInjuries.length > 0 && <span className="text-warn ml-1 text-[9px]">⚠</span>}
          </span>
          {(isLive || isPost) && homeScore !== undefined ? (
            <span className={clsx('text-sm font-bold tabular-nums', isPost && homeScore > (awayScore ?? 0) ? 'text-win' : 'text-gray-400')}>
              {homeScore}
            </span>
          ) : odds?.homeMoneyline !== undefined ? (
            <span className="text-[10px] font-mono text-gray-500 tabular-nums flex-shrink-0">
              {odds.homeMoneyline > 0 ? '+' : ''}{odds.homeMoneyline}
            </span>
          ) : null}
        </div>
      </div>

      {bestRec && bestRec.edge && bestRec.edge > 0 && (
        <div className="mt-1.5 flex items-center gap-1">
          <span className="text-[9px] text-gray-600 uppercase tracking-wide">Edge</span>
          <span className="text-[10px] font-mono text-warn tabular-nums">+{(bestRec.edge * 100).toFixed(1)}%</span>
          {bestRec.market && <span className="text-[9px] text-gray-700 truncate ml-auto">{bestRec.market}</span>}
        </div>
      )}
    </button>
  );
}

// ── Compact NBA slate card ────────────────────────────────────────────────────

function NBASlateCard({
  context,
  recommendations,
  injuries,
  auditStatus,
  isActive,
  onClick,
}: {
  context: NBAGameContext;
  recommendations: BetRecommendation[];
  injuries: NBAInjuryReport[];
  auditStatus?: string;
  isActive: boolean;
  onClick: () => void;
}) {
  const { game, odds } = context;
  const { awayTeam, homeTeam, status, awayScore, homeScore } = game;
  const isLive = status.state === 'in';
  const isPost = status.state === 'post';

  const strongRec = recommendations.find((r) => r.decisionStatus === 'STRONG_BET');
  const qualifiedRec = recommendations.find((r) => r.decisionStatus === 'QUALIFIED_BET');
  const bestRec = strongRec ?? qualifiedRec;

  const awayLoadManaged = context.awayRestProfile?.hasLoadManagementRisk;
  const homeLoadManaged = context.homeRestProfile?.hasLoadManagementRisk;
  const awayInjuries = injuries.filter((i) => i.teamId === awayTeam.id && (i.status === 'Out' || i.status === 'Questionable') && !i.isLoadManagement);
  const homeInjuries = injuries.filter((i) => i.teamId === homeTeam.id && (i.status === 'Out' || i.status === 'Questionable') && !i.isLoadManagement);

  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left px-3 py-2.5 border-b border-surface-4 transition-colors',
        isActive
          ? 'bg-brand/8 border-l-2 border-l-brand'
          : 'hover:bg-surface-1 border-l-2 border-l-transparent',
      )}
    >
      {/* Status + playoff badge + rec badge */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          {isLive ? (
            <>
              <span className="w-1.5 h-1.5 rounded-full bg-win animate-pulse flex-shrink-0" />
              <span className="text-[10px] font-semibold text-win">
                {status.period ? `Q${status.period}` : 'Live'}{status.clock ? ` ${status.clock}` : ''}
              </span>
            </>
          ) : (
            <span className={clsx('text-[10px] font-medium', isPost ? 'text-gray-600' : 'text-gray-500')}>
              {isPost ? 'Final' : status.detail || 'Scheduled'}
            </span>
          )}
          {game.isPlayoff && (
            <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-brand/10 border border-brand/25 text-brand-light">
              {game.seriesGameNumber ? `G${game.seriesGameNumber}` : 'PO'}
            </span>
          )}
        </div>
        {bestRec ? (
          <span className={clsx(
            'text-[9px] font-bold px-1 py-0.5 rounded border',
            strongRec ? 'bg-win/15 border-win/40 text-win' : 'bg-brand/15 border-brand/40 text-brand-light',
          )}>
            {strongRec ? 'STRONG' : 'QUALIFIED'}
          </span>
        ) : auditStatus && AUDIT_BADGE[auditStatus] ? (
          <span className={clsx('text-[9px] font-bold px-1 py-0.5 rounded border', AUDIT_BADGE[auditStatus].cls)}>
            {AUDIT_BADGE[auditStatus].label}
          </span>
        ) : null}
      </div>

      {/* Teams */}
      <div className="space-y-0.5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-300 truncate mr-2">
            {awayTeam.abbreviation}
            <span className="text-gray-500 ml-1 font-normal text-[10px]">{awayTeam.name.split(' ').pop()}</span>
            {awayLoadManaged && <span className="ml-1 text-[9px] text-orange-400 font-bold" title="Load management risk">LM</span>}
            {awayInjuries.length > 0 && <span className="text-warn ml-1 text-[9px]">⚠</span>}
          </span>
          {(isLive || isPost) && awayScore !== undefined ? (
            <span className={clsx('text-sm font-bold tabular-nums', isPost && awayScore > (homeScore ?? 0) ? 'text-win' : 'text-gray-400')}>
              {awayScore}
            </span>
          ) : odds?.awayMoneyline !== undefined ? (
            <span className="text-[10px] font-mono text-gray-500 tabular-nums flex-shrink-0">
              {odds.awayMoneyline > 0 ? '+' : ''}{odds.awayMoneyline}
            </span>
          ) : null}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-300 truncate mr-2">
            {homeTeam.abbreviation}
            <span className="text-gray-500 ml-1 font-normal text-[10px]">{homeTeam.name.split(' ').pop()}</span>
            {homeLoadManaged && <span className="ml-1 text-[9px] text-orange-400 font-bold" title="Load management risk">LM</span>}
            {homeInjuries.length > 0 && <span className="text-warn ml-1 text-[9px]">⚠</span>}
          </span>
          {(isLive || isPost) && homeScore !== undefined ? (
            <span className={clsx('text-sm font-bold tabular-nums', isPost && homeScore > (awayScore ?? 0) ? 'text-win' : 'text-gray-400')}>
              {homeScore}
            </span>
          ) : odds?.homeMoneyline !== undefined ? (
            <span className="text-[10px] font-mono text-gray-500 tabular-nums flex-shrink-0">
              {odds.homeMoneyline > 0 ? '+' : ''}{odds.homeMoneyline}
            </span>
          ) : null}
        </div>
      </div>

      {bestRec && bestRec.edge && bestRec.edge > 0 && (
        <div className="mt-1.5 flex items-center gap-1">
          <span className="text-[9px] text-gray-600 uppercase tracking-wide">Edge</span>
          <span className="text-[10px] font-mono text-warn tabular-nums">+{(bestRec.edge * 100).toFixed(1)}%</span>
          {bestRec.market && <span className="text-[9px] text-gray-700 truncate ml-auto">{bestRec.market}</span>}
        </div>
      )}
    </button>
  );
}

// ── CourtEdgeWorkspace ────────────────────────────────────────────────────────

function changeDate(current: string, delta: number): string {
  const d = new Date(current + 'T12:00:00');
  return format(delta > 0 ? addDays(d, delta) : subDays(d, Math.abs(delta)), 'yyyy-MM-dd');
}

export function CourtEdgeWorkspace() {
  const [date, setDate] = useState(todayPST());
  const [activeLeague, setActiveLeague] = useState<ActiveLeague>('wnba');
  const { activeGameId, openGameCanvas } = useOSStore();
  const hasAutoSelected = useRef(false);

  // WNBA data — slate always fetches (needed for auto-select logic)
  const { data: wnbaSlate = [], isLoading: wnbaSlateLoading, refetch: refetchWNBASlate, isFetching: wnbaFetching } = useQuery({
    queryKey: ['wnba-slate-workspace', date],
    queryFn: () => wnbaApi.getSlate(date),
    staleTime: 60_000,
  });
  const { data: wnbaRecommendations = [] } = useQuery({
    queryKey: ['wnba-recs-workspace', date],
    queryFn: () => wnbaApi.getRecommendations(date),
    staleTime: 120_000,
    retry: false,
    enabled: activeLeague === 'wnba',
  });
  const { data: wnbaInjuries = [] } = useQuery({
    queryKey: ['wnba-injuries-workspace', date],
    queryFn: () => wnbaApi.getInjuries(date),
    staleTime: 30 * 60_000,
    retry: false,
    enabled: activeLeague === 'wnba',
  });

  // NBA data — slate always fetches (needed for auto-select logic)
  const { data: nbaSlate = [], isLoading: nbaSlateLoading, refetch: refetchNBASlate, isFetching: nbaFetching } = useQuery({
    queryKey: ['nba-slate-workspace', date],
    queryFn: () => nbaApi.getSlate(date),
    staleTime: 60_000,
  });
  const { data: nbaRecommendations = [] } = useQuery({
    queryKey: ['nba-recs-workspace', date],
    queryFn: () => nbaApi.getRecommendations(date),
    staleTime: 120_000,
    retry: false,
    enabled: activeLeague === 'nba',
  });
  const { data: nbaInjuries = [] } = useQuery({
    queryKey: ['nba-injuries-workspace', date],
    queryFn: () => nbaApi.getInjuries(date),
    staleTime: 30 * 60_000,
    retry: false,
    enabled: activeLeague === 'nba',
  });

  // Audit candidates — non-actionable rows for badge display on slate cards
  const { data: wnbaAuditCandidates = [] } = useQuery({
    queryKey: ['wnba-audit-workspace', date],
    queryFn: () => wnbaApi.getAuditCandidates(date),
    staleTime: 120_000,
    retry: false,
    enabled: activeLeague === 'wnba',
  });
  const { data: nbaAuditCandidates = [] } = useQuery({
    queryKey: ['nba-audit-workspace', date],
    queryFn: () => nbaApi.getAuditCandidates(date),
    staleTime: 120_000,
    retry: false,
    enabled: activeLeague === 'nba',
  });

  // Auto-select the league that has games today, but only on first mount.
  useEffect(() => {
    if (hasAutoSelected.current) return;
    if (!wnbaSlateLoading && !nbaSlateLoading) {
      hasAutoSelected.current = true;
      if (wnbaSlate.length === 0 && nbaSlate.length > 0) {
        setActiveLeague('nba');
      }
    }
  }, [wnbaSlate, nbaSlate, wnbaSlateLoading, nbaSlateLoading]);

  const isToday = date === todayPST();
  const isWNBA = activeLeague === 'wnba';

  const slate = isWNBA ? wnbaSlate : nbaSlate;
  const recommendations = isWNBA ? wnbaRecommendations : nbaRecommendations;
  const slateLoading = isWNBA ? wnbaSlateLoading : nbaSlateLoading;
  const isFetching = isWNBA ? wnbaFetching : nbaFetching;
  const refetchSlate = isWNBA ? refetchWNBASlate : refetchNBASlate;

  const liveGames = slate.filter((ctx) => ctx.game.status.state === 'in');
  const qualifiedRecs = recommendations.filter(
    (r) => r.decisionStatus === 'STRONG_BET' || r.decisionStatus === 'QUALIFIED_BET',
  );

  // Build a map of eventPk → best non-actionable status for slate badge display
  const auditCandidates = isWNBA ? wnbaAuditCandidates : nbaAuditCandidates;
  const statusPriority: Record<string, number> = { LEAN: 0, NO_BET: 1, INVALID: 2 };
  const auditStatusMap = new Map<string, string>();
  for (const r of auditCandidates) {
    if (!r.eventPk || r.isActionable) continue;
    const existing = auditStatusMap.get(r.eventPk);
    if (!existing || (statusPriority[r.decisionStatus] ?? 99) < (statusPriority[existing] ?? 99)) {
      auditStatusMap.set(r.eventPk, r.decisionStatus);
    }
  }

  // NBA: count load management alerts across today's slate
  const loadMgmtCount = !isWNBA
    ? (nbaSlate as NBAGameContext[]).filter(
        (ctx) => ctx.awayRestProfile?.hasLoadManagementRisk || ctx.homeRestProfile?.hasLoadManagementRisk,
      ).length
    : 0;

  // ── Slate panel ──────────────────────────────────────────────────────────────

  const slatePanel = (
    <div className="flex flex-col h-full overflow-hidden bg-surface-0">
      {/* Slate header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-surface-4 bg-surface-1/30 flex-shrink-0">
        <CircleDot className="w-3.5 h-3.5 text-warn flex-shrink-0" />
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 flex-1">Game Slate</p>
        <span className="text-[10px] text-gray-600 font-mono">{slate.length} games</span>
        {(slateLoading || isFetching) && <RefreshCw className="w-3 h-3 text-gray-600 animate-spin flex-shrink-0" />}
      </div>

      {/* Date navigation */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-surface-4 flex-shrink-0">
        <button onClick={() => setDate((d) => changeDate(d, -1))} className="p-1 rounded text-gray-500 hover:text-gray-200 hover:bg-surface-2 transition-colors">
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => !isToday && setDate(todayPST())}
          className={clsx('flex-1 text-center text-[11px] font-medium transition-colors', isToday ? 'text-gray-300' : 'text-warn hover:text-warn/80 cursor-pointer')}
        >
          {format(new Date(date + 'T12:00:00'), 'MMM d, EEE')}
          {isToday && <span className="ml-1 text-[9px] text-warn font-bold">TODAY</span>}
        </button>
        <button onClick={() => setDate((d) => changeDate(d, 1))} className="p-1 rounded text-gray-500 hover:text-gray-200 hover:bg-surface-2 transition-colors">
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Game list */}
      <div className="flex-1 overflow-y-auto">
        {slateLoading && slate.length === 0 ? (
          <div className="flex flex-col gap-1.5 p-3">
            {[1, 2, 3].map((i) => <SkeletonGameCard key={i} />)}
          </div>
        ) : slate.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-4">
            <Activity className="w-6 h-6 text-gray-700" />
            <p className="text-xs text-gray-600">No {isWNBA ? 'WNBA' : 'NBA'} games scheduled.</p>
          </div>
        ) : isWNBA ? (
          (wnbaSlate as WNBAGameContext[]).map((ctx) => (
            <WNBASlateCard
              key={ctx.game.eventPk}
              context={ctx}
              recommendations={recommendations.filter((r) => r.eventPk === ctx.game.eventPk)}
              injuries={wnbaInjuries}
              isActive={activeGameId === ctx.game.eventPk}
              onClick={() => openGameCanvas(ctx.game.eventPk, 'wnba')}
              auditStatus={auditStatusMap.get(ctx.game.eventPk)}
            />
          ))
        ) : (
          (nbaSlate as NBAGameContext[]).map((ctx) => (
            <NBASlateCard
              key={ctx.game.eventPk}
              context={ctx}
              recommendations={recommendations.filter((r) => r.eventPk === ctx.game.eventPk)}
              injuries={nbaInjuries}
              isActive={activeGameId === ctx.game.eventPk}
              onClick={() => openGameCanvas(ctx.game.eventPk, 'nba')}
              auditStatus={auditStatusMap.get(ctx.game.eventPk)}
            />
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full overflow-hidden bg-surface-0">
      {/* Workspace header */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-surface-4 bg-surface-1/50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-warn/10 border border-warn/20">
            <CircleDot className="w-3.5 h-3.5 text-warn" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-100 leading-tight">CourtEdge</p>
            <p className="text-[9px] text-gray-500 uppercase tracking-widest leading-tight">Basketball Intelligence</p>
          </div>
        </div>

        {/* League toggle */}
        <div className="flex items-center gap-0.5 bg-surface-2 rounded-md p-0.5 ml-2">
          <button
            onClick={() => setActiveLeague('wnba')}
            className={clsx(
              'text-[10px] font-bold px-2 py-1 rounded transition-colors',
              isWNBA
                ? 'bg-warn/20 text-warn border border-warn/30'
                : 'text-gray-500 hover:text-gray-300',
            )}
          >
            WNBA
          </button>
          <button
            onClick={() => setActiveLeague('nba')}
            className={clsx(
              'text-[10px] font-bold px-2 py-1 rounded transition-colors',
              !isWNBA
                ? 'bg-brand/20 text-brand-light border border-brand/30'
                : 'text-gray-500 hover:text-gray-300',
            )}
          >
            NBA
          </button>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {!isWNBA && loadMgmtCount > 0 && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-orange-500/10 border border-orange-500/25 text-[10px] font-bold text-orange-400">
              {loadMgmtCount} LM
            </span>
          )}
          {liveGames.length > 0 && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-win/10 border border-win/25 text-[10px] font-bold text-win">
              <span className="w-1 h-1 rounded-full bg-win animate-pulse" />
              {liveGames.length} Live
            </span>
          )}
          {qualifiedRecs.length > 0 && (
            <span className="px-2 py-1 rounded-md bg-warn/10 border border-warn/25 text-[10px] font-semibold text-warn">
              {qualifiedRecs.length} Qualified
            </span>
          )}
          <button
            onClick={() => void refetchSlate()}
            disabled={isFetching}
            className="flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-gray-200 hover:bg-surface-2 transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={clsx('w-3.5 h-3.5', isFetching && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* 3-panel workspace */}
      <ModuleWorkspace
        slatePanel={slatePanel}
        canvas={<GameIntelligenceCanvas />}
        uplinkPanel={
          <PulseUplink
            module={isWNBA ? 'courtedge' : 'courtedge-nba'}
            recCount={qualifiedRecs.length}
            betNowCount={qualifiedRecs.filter((r) => r.decisionStatus === 'STRONG_BET').length}
          />
        }
      />
    </div>
  );
}
